import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, merge, Observable, throwError } from 'rxjs';
import { catchError, exhaustMap, mapTo, switchMap, switchMapTo, take } from 'rxjs/operators';
import * as AuthenticationActions from '../authentication/state/authentication.actions';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
   excludeUrls = ['/api/session/status', '/api/authentication/login'];

   // Returns an observable that will either emit a single value and complete (if the user successfully reauthenticates)
   // or complete without emitting a value (if the user cancels reauthentication).
   private reauthenticated$ = this.actions$.pipe(
      ofType(AuthenticationActions.reAuthenticationRequired),
      // this exhaustMap essentially 'queues' all requests that received a 401 response behind a single login dialog
      exhaustMap(() => {
         // If the same user's reauthentication is verified, we emit an arbitrary value.
         const reauth = this.actions$.pipe(ofType(AuthenticationActions.reAuthenticationSuccess), mapTo(undefined));
         // If the user closes the login dialog we return EMPTY to complete without emitting a value.
         const reauthCancelled = this.actions$.pipe(
            ofType(AuthenticationActions.reAuthenticationCancel),
            switchMapTo(EMPTY)
         );
         const reauthResult = merge(reauth, reauthCancelled).pipe(take(1));
         return reauthResult;
      })
   );

   constructor(private store: Store, private actions$: Actions) {}

   intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      const skipIntercept = this.excludeUrls.includes(request.url);

      if (skipIntercept) {
         return next.handle(request);
      }

      return next.handle(request).pipe(
         catchError((error: HttpErrorResponse) => {
            return this.handleResponseError(error, request, next);
         })
      );
   }

   reauthenticate(): Observable<any> {
      setTimeout(() => {
         this.store.dispatch(AuthenticationActions.reAuthenticationRequired());
      });

      return this.reauthenticated$;
   }

   private handleResponseError(
      error: HttpErrorResponse | any,
      request: HttpRequest<any>,
      next: HttpHandler
   ): Observable<any> {
      if (error.status === 401 || error.Exception === 'AuthenticationException') {
         return this.reauthenticate().pipe(
            // if this.reauthenticate emits a value (indicating that the user successfully reauthenticated), then we retry the request.
            // Because NDT stores authentication information in cookies, we don't have to rebuild the request to update authentication
            // headers - the browser will do this for us automatically.
            take(1),
            switchMap(() => {
               return next.handle(request);
            })
         );
      }
      return throwError(error);
   }
}
